/**
 * Organization Info Cards Component
 *
 * Displays organization information in a card grid layout.
 */

import type { ReactNode } from 'react';
import { Business, CreditCard, People } from '@mui/icons-material';
import { Box, Card, CardContent, CardHeader, Chip, Grid, Typography } from '@mui/material';
import type { ProviderOrganizationResponse } from '@store/generated/organizations';

import type { OrganizationMemberTableRow } from '../types';

import { OrganizationNameField } from './OrganizationNameField';
import { SectionCardTitle } from './SectionCardTitle';

type InfoCardsVariant = 'full' | 'members' | 'subscription' | 'info';

interface OrganizationInfoCardsProps {
  organization?: ProviderOrganizationResponse;
  activeMembers: OrganizationMemberTableRow[];
  inactiveMembers: OrganizationMemberTableRow[];
  userEmail?: string;
  /**
   * Controls which cards are displayed:
   * - 'full': All cards (Organization Info, Members, Subscription)
   * - 'members': Organization Info + Members cards
   * - 'subscription': Organization Info + Subscription cards
   * - 'info': Organization Info card only
   */
  variant?: InfoCardsVariant;
  /** Practice admins can rename the org in place (GDN-2441). */
  canEditName?: boolean;
  /** Guide control for the Organization Info card, inline after its title (GDN-3703). */
  titleControl?: ReactNode;
}

export function OrganizationInfoCards({
  organization,
  activeMembers,
  inactiveMembers,
  userEmail,
  variant = 'full',
  canEditName = false,
  titleControl,
}: OrganizationInfoCardsProps) {
  if (!organization) return null;

  const showMembers = variant === 'full' || variant === 'members';
  const showSubscription = variant === 'full' || variant === 'subscription';

  // Calculate grid size based on number of cards shown
  const cardCount = 1 + (showMembers ? 1 : 0) + (showSubscription ? 1 : 0);
  const gridSize = cardCount === 3 ? 4 : cardCount === 2 ? 6 : 12;

  return (
    <Grid container spacing={3} sx={{ mb: 3 }}>
      {/* Organization Info Card */}
      <Grid size={{ xs: 12, md: gridSize }}>
        <Card elevation={1} sx={{ borderRadius: 2, border: 1, borderColor: 'grey.200' }}>
          <CardHeader
            avatar={<Business color="primary" />}
            title={<SectionCardTitle title="Organization Info" control={titleControl} />}
            slotProps={{ title: { variant: 'h6' } }}
          />
          <CardContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {canEditName ? (
                <OrganizationNameField name={organization.name} />
              ) : (
                <div>
                  <Typography variant="body2" color="text.secondary">
                    Name
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                    {organization.name}
                  </Typography>
                </div>
              )}
              {organization.created_at && (
                <div>
                  <Typography variant="body2" color="text.secondary">
                    Created
                  </Typography>
                  <Typography variant="body1">
                    {new Date(organization.created_at).toLocaleDateString()}
                  </Typography>
                </div>
              )}
            </Box>
          </CardContent>
        </Card>
      </Grid>

      {/* Members Card */}
      {showMembers && (
        <Grid size={{ xs: 12, md: gridSize }}>
          <Card>
            <CardHeader
              avatar={<People color="primary" />}
              title="Members"
              slotProps={{ title: { variant: 'h6' } }}
            />
            <CardContent>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <div>
                  <Typography variant="body2" color="text.secondary">
                    Active Members
                  </Typography>
                  <Typography variant="h4" color="primary.main">
                    {activeMembers.length}
                  </Typography>
                </div>
                {inactiveMembers.length > 0 && (
                  <div>
                    <Typography variant="body2" color="text.secondary">
                      Inactive Members
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      {inactiveMembers.length}
                    </Typography>
                  </div>
                )}
                {organization.seat_count !== null && (
                  <div>
                    <Typography variant="body2" color="text.secondary">
                      Available Seats
                    </Typography>
                    <Typography variant="body1">
                      {(organization.seat_count ?? 0) - (organization.member_count || 0)} of{' '}
                      {organization.seat_count}
                    </Typography>
                  </div>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      )}

      {/* Subscription Card */}
      {showSubscription && (
        <Grid size={{ xs: 12, md: gridSize }}>
          <Card elevation={1} sx={{ borderRadius: 2, border: 1, borderColor: 'grey.200' }}>
            <CardHeader
              avatar={<CreditCard color="primary" />}
              title="Subscription"
              slotProps={{ title: { variant: 'h6' } }}
            />
            <CardContent>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <div>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Status
                  </Typography>
                  {organization.subscription_status ? (
                    <Chip
                      label={organization.subscription_status}
                      color={
                        organization.subscription_status === 'active'
                          ? 'success'
                          : organization.subscription_status === 'trialing'
                            ? 'info'
                            : organization.subscription_status === 'canceled'
                              ? 'error'
                              : 'default'
                      }
                      size="small"
                    />
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No active subscription
                    </Typography>
                  )}
                </div>
                {organization.seat_count !== null && (
                  <div>
                    <Typography variant="body2" color="text.secondary">
                      Total Seats
                    </Typography>
                    <Typography variant="body1">{organization.seat_count} seats</Typography>
                  </div>
                )}
                <div>
                  <Typography variant="body2" color="text.secondary">
                    Billing Email
                  </Typography>
                  <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                    {userEmail || 'Not set'}
                  </Typography>
                </div>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      )}
    </Grid>
  );
}
